export function formatDate(
	date: Date,
	options: Intl.DateTimeFormatOptions = {
		year: 'numeric',
		month: 'long',
		day: 'numeric',
	},
	locales: Intl.LocalesArgument = 'en-US',
): string {
	if (!date) return 'Unknown'

	return new Date(date).toLocaleDateString(locales, options)
}

export interface DateRangeDto {
	from: Date
	to: Date
}

export enum StepType {
	SECONDS = 'SECONDS',
	MINUITES = 'MINUITES',
	HOURS = 'HOURS',
	DAYS = 'DAYS',
	WEEKS = 'WEEKS',
	MONHTHS = 'MONTHS',
	YEARS = 'YEARS',
}

export enum SubscriptionFrequency {
	DAILY = 1,
	WEEKLY = 7,
	BIWEEKLY = 14,
	MONTHLY = 30,
	BIMONTHLY = 60,
	QUARTERLY = 90,
	BIQUARTERLY = 180,
	YEARLY = 365,
}

/**
 * A numerical representation of time in days
 *
 * Example usage: twice a day = Frequency.DAILY * 2
 */

export enum FrequencyPerYear {
	DAILY = 365,
	TWICE_PER_WEEK = 104,
	WEEKLY = 52,
	TWICE_PER_MONTH = 24,
	MONTHLY = 12,
	QUARTERLY = 4,
	YEARLY = 1,
	NONE = 0,
}

export enum DayNames {
	'Monday',
	'Tuesday',
	'Wednesday',
	'Thursday',
	'Friday',
	'Saturday',
	'Sunday',
}

export enum month_names {
	'January',
	'February',
	'March',
	'April',
	'May',
	'June',
	'July',
	'August',
	'September',
	'October',
	'November',
	'December',
}

export enum month_names_short {
	'Jan',
	'Feb',
	'Mar',
	'Apr',
	'May',
	'Jun',
	'Jul',
	'Aug',
	'Sep',
	'Oct',
	'Nov',
	'Dec',
}

const nth = function (d: number): string {
	if (d > 3 && d < 21) return 'th'
	switch (d % 10) {
		case 1:
			return 'st'
		case 2:
			return 'nd'
		case 3:
			return 'rd'
		default:
			return 'th'
	}
}

export function format(date: Date, format: string): string {
	date = new Date(date)

	const month = (date.getMonth() + 1).toString()
	const day = date.getDate().toString()
	const year = date.getFullYear().toString()
	const hours = date.getHours().toString()
	const minutes = date.getMinutes().toString()
	const seconds = date.getSeconds().toString()
	const milliseconds = date.getMilliseconds().toString()

	if (format === 'date') format = 'YYYY-MM-DD'
	if (format === 'time') format = 'HH:mm:ss'
	if (format === 'datetime') format = 'YYYY-MM-DD HH:mm:ss'

	return format
		.replace('YYYY', year)
		.replace('YY', year.substring(4, 2))
		.replace('MMMM', month_names[date.getMonth()])
		.replace('MMM', month_names_short[date.getMonth()])
		.replace('MM', month.length === 1 ? '0' + month : month)
		.replace('DD', day.length === 1 ? '0' + day : day)
		.replace('Do', day + nth(parseInt(day)))
		.replace('Day', this.dayName(date))
		.replace('HH', hours.length === 1 ? '0' + hours : hours)
		.replace('mm', minutes.length === 1 ? '0' + minutes : minutes)
		.replace('ss', seconds.length === 1 ? '0' + seconds : seconds)
		.replace(
			'SSS',
			milliseconds.length === 1
				? '00' + milliseconds
				: milliseconds.length === 2
				? '0' + milliseconds
				: milliseconds,
		)
}

export function dayName(date: Date): string {
	let day = date.getDay() - 1
	if (day === -1) day = 6
	return DayNames[day]
}

export function formatZerolessValue(value: number): string {
	if (value < 10) return '0' + value

	return String(value)
}

export function addDays(date: Date, days: number): Date {
	const result = new Date(date)
	result.setDate(result.getDate() + days)
	return result
}

/**
 * Takes a date, adds steps to it and returns the new date
 */
export function addStep(date: Date, step: StepType, steps?: number): Date {
	if (!steps) {
		steps = 1
	}

	switch (step) {
		case StepType.HOURS:
			date.setHours(date.getHours() + Number(steps))
			return date

		case StepType.DAYS:
			date.setDate(date.getDate() + Number(steps))
			return date

		case StepType.WEEKS:
			date.setDate(date.getDate() + Number(steps * 7))
			return date

		case StepType.MONHTHS:
			date.setMonth(date.getMonth() + Number(steps))
			return date

		case StepType.YEARS:
			date.setFullYear(date.getFullYear() + Number(steps))
			return date
	}
}

export function lastMonth(): DateRangeDto {
	const start_date = new Date()
	const end_date = new Date()

	start_date.setFullYear(start_date.getFullYear())
	start_date.setMonth(start_date.getMonth() - 1)
	start_date.setDate(1)

	end_date.setFullYear(end_date.getFullYear())
	end_date.setMonth(end_date.getMonth())
	end_date.setDate(0)

	start_date.setHours(0)
	start_date.setMinutes(0)
	start_date.setSeconds(0)
	start_date.setMilliseconds(0)

	end_date.setHours(23)
	end_date.setMinutes(59)
	end_date.setSeconds(59)
	end_date.setMilliseconds(59)

	return {
		from: start_date,
		to: end_date,
	}
}

/**
 * Checks if date is between two other dates
 */
export function isBetween(date: Date, from: Date, to: Date): boolean {
	const isBetween = (date: any, min: any, max: any) =>
		date.getTime() >= min.getTime() && date.getTime() <= max.getTime()
	return isBetween(date, from, to)
}

export function nextDate(frequency: SubscriptionFrequency): Date {
	switch (frequency) {
		case SubscriptionFrequency.DAILY:
			const day = new Date()
			day.setDate(day.getDate() + 1)
			return day
		case SubscriptionFrequency.WEEKLY:
			const week = new Date()
			week.setDate(week.getDate() + 7)
			return week
		case SubscriptionFrequency.BIWEEKLY:
			const biweek = new Date()
			biweek.setDate(biweek.getDate() + 14)
			return biweek
		case SubscriptionFrequency.MONTHLY:
			const monthly = new Date()
			monthly.setMonth(monthly.getMonth() + 1)
			return monthly
		case SubscriptionFrequency.BIMONTHLY:
			const bimonthly = new Date()
			bimonthly.setMonth(bimonthly.getMonth() + 2)
			return bimonthly
		case SubscriptionFrequency.QUARTERLY:
			const quarterly = new Date()
			quarterly.setMonth(quarterly.getMonth() + 3)
			return quarterly
		case SubscriptionFrequency.BIQUARTERLY:
			const biquarterly = new Date()
			biquarterly.setMonth(biquarterly.getMonth() + 6)
			return biquarterly
		case SubscriptionFrequency.YEARLY:
			const yearly = new Date()
			yearly.setFullYear(yearly.getFullYear() + 1)
			return yearly
	}
}

export function minutesAgo(minutes: number): Date {
	return new Date(new Date().getTime() - minutes * 60000) // minutes * 60 seconds * 1000 milliseconds
}

export function daysAgo(days: number): Date {
	return new Date(new Date().getTime() - 1000 * 60 * 60 * 24 * days)
}

/**
 * Returns the time between two dates with the format "x {second(s)|minute(s)|hour(s)|month(s)|year(s)} ago"
 *
 * Optionally you can max a max object with the following properties:
 * type: StepType
 * format: string
 *
 * If the date is larger than the max type, it will return the date in the format specified
 */

export function ago(
	date: Date,
	decimals: number = 0,
	max?: {
		type: StepType
		format: string
	},
): string {
	if (!date) return 'Unknown'

	const seconds = Math.floor((new Date().getTime() - new Date(date).getTime()) / 1000)

	let interval = Math.floor(seconds / 31536000)

	if (interval >= 1) {
		if (max?.type === StepType.MONHTHS) return format(date, max.format)
		if (interval === 1) return '1 year ago'
		return interval + ' years ago'
	}

	interval = Math.floor(seconds / 2592000)
	if (interval >= 1) {
		if (max?.type === StepType.WEEKS) return format(date, max.format)
		if (interval === 1) return '1 month ago'
		return interval + ' months ago'
	}

	interval = Math.floor(seconds / 604800)
	if (interval >= 1) {
		if (max?.type === StepType.DAYS) return format(date, max.format)
		if (interval === 1) return '1 week ago'
		return interval + ' weeks ago'
	}

	interval = Math.floor(seconds / 86400)
	if (interval >= 1) {
		if (max?.type === StepType.HOURS) return format(date, max.format)
		if (interval === 1) return '1 day ago'
		return interval + ' days ago'
	}

	interval = Math.floor(seconds / 3600)
	if (interval >= 1) {
		if (max?.type === StepType.MINUITES) return format(date, max.format)
		if (interval === 1) return '1 hour ago'
		return (seconds / 3600).toFixed(decimals) + ' hours ago'
	}

	interval = Math.floor(seconds / 60)
	if (interval >= 1) {
		if (max?.type === StepType.SECONDS) return format(date, max.format)
		if (interval === 1) return '1 minute ago'
		return (seconds / 60).toFixed(decimals) + ' minute ago'
	}

	if (seconds <= 1) {
		return '1 second ago'
	}

	return Math.floor(seconds) + ' seconds ago'
}
